import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, X, Trash2, Loader2, Sparkles } from "lucide-react";

export default function CopilotChat({ hospitalId, hospitalName }) {
  const { t } = useTranslation();
  // Auth handled by httpOnly cookies
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (historyLoaded) return;
    try {
      const res = await axios.get(`${API}/copilot/history/${hospitalId}`);
      setMessages(res.data);
      setHistoryLoaded(true);
    } catch {
      toast.error("Error loading chat history");
    }
  }, [hospitalId, historyLoaded]);

  useEffect(() => {
    if (open && !historyLoaded) loadHistory();
  }, [open, loadHistory, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/copilot/chat`, {
        hospital_id: hospitalId,
        message: text,
      });
      setMessages(prev => [...prev, res.data]);
    } catch (e) {
      const errMsg = e.response?.data?.detail || t("copilot.error");
      toast.error(errMsg);
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
      setInput(text);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/copilot/history/${hospitalId}`);
      setMessages([]);
      toast.success(t("copilot.cleared"));
    } catch {
      toast.error("Error");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating button
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="copilot-toggle-btn"
        className="fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-slate-900 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center group"
      >
        <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 right-6 z-50 w-[380px] h-[540px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
      data-testid="copilot-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-none">{t("copilot.title")}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{hospitalName}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={clearHistory} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" title={t("copilot.clear")} data-testid="copilot-clear-btn">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="copilot-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="copilot-messages">
        {messages.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
              <Sparkles className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700">{t("copilot.welcome")}</p>
            <p className="text-xs text-slate-400 mt-1 max-w-[260px]">{t("copilot.welcomeHint")}</p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {[t("copilot.suggestion1"), t("copilot.suggestion2"), t("copilot.suggestion3")].map((s, idx) => (
                <button
                  key={s}
                  onClick={() => { setInput(s); }}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                  data-testid={`copilot-suggestion-${idx}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                msg.role === "user"
                  ? "bg-slate-900 text-white rounded-br-md"
                  : "bg-slate-100 text-slate-800 rounded-bl-md"
              }`}
              data-testid={`copilot-msg-${msg.role}`}
            >
              <div className="whitespace-pre-wrap break-words">{msg.content}</div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                <span className="text-xs text-slate-400">{t("copilot.thinking")}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("copilot.placeholder")}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
            data-testid="copilot-input"
          />
          <Button
            size="sm"
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            className="h-9 w-9 rounded-xl bg-slate-900 hover:bg-slate-800 p-0 shrink-0"
            data-testid="copilot-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-slate-300 mt-1.5 text-center">{t("copilot.disclaimer")}</p>
      </div>
    </div>
  );
}
